import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { css, StyleDeclarationValue } from 'aphrodite';
import { makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { v4 as uuidgen } from 'uuid';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { baseStyleSheet } from '../../styles/styles';
import { Button } from '../Button';
import { asModalComponent } from '../Modal';
import { Input } from '../../components-v2/Input';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IModalContext<string> {
	className?: string;
	cta?: ((props: { onClick: (e: React.MouseEvent<HTMLElement>) => void }) => React.ReactNode) | string;
	initialValue?: string;
	inputLabel?: React.ReactNode;
	isValid?(value: string): boolean;
	placeholderText?: string;
	styles?: StyleDeclarationValue[];
	title?: React.ReactNode;
	type?: 'password' | 'text' | 'email';
	children?: React.ReactNode;
}
interface IState {
	buttonDisabled: boolean;
}

class _TextInputModal extends React.Component<IProps, IState> {
	public static defaultProps: Partial<IProps> = {
		type: 'text',
	};
	private mText: string;
	private mUuid = uuidgen();

	constructor(props: IProps) {
		super(props);

		makeObservable<_TextInputModal, 'mText'>(this, {
			mText: observable,
		});

		this.mText = props.initialValue;
		this.state = {
			buttonDisabled: false,
		};
	}

	public render() {
		const { className, styles, title, children, type, cta, placeholderText, inputLabel } = this.props;
		const overrideCta = typeof cta === 'function' ? cta({ onClick: this.onCtaClicked }) : null;
		const ctaContent = !overrideCta && typeof cta === 'string' ? <span>{cta}</span> : null;
		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} text-input-modal ${className || ''}`}>
				<div className={css(baseStyleSheet.truncateText, styleSheet.header)}>{title}</div>
				<div className={css(baseStyleSheet.verticalStack)}>
					<div>
						{inputLabel}
						<Input
							id={`input-${this.mUuid}`}
							onChange={this.onTextChanged}
							placeholder={placeholderText}
							type={type}
							value={this.mText || ''}
						/>
					</div>
					{children}
				</div>
				<div className={css(baseStyleSheet.horizontalStack)}>
					{overrideCta || (
						<Button
							kind='primary'
							className={css(styleSheet.ctaButton)}
							onClick={this.onCtaClicked}
							disabled={this.state.buttonDisabled}
							isLoading={this.state.buttonDisabled}
						>
							{ctaContent || <span>Ok</span>}
						</Button>
					)}
				</div>
			</div>
		);
	}

	private onTextChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { isValid } = this.props;
		const v = e.target.value;
		if (isValid?.(v) || !isValid) {
			this.mText = v || '';
		}
	};

	private onCtaClicked = () => {
		const { parentModal } = this.props;
		this.setState({ buttonDisabled: true });
		parentModal.onRequestClose(this.mText, false);
	};
}

const TextInputModalAsObserver = observer(_TextInputModal);
const TextInputModalWithContext = inject(ModalChildComponentContextKey)(TextInputModalAsObserver);
const TextInputModalWithLogging = withEventLogging(TextInputModalWithContext, 'TextInputModal');
export const TextInputModal = asModalComponent(TextInputModalWithLogging, {
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
